/* global ElementUtil */
import {ElementUtil} from './elementUtil.js';

/**
 * Read a toolbar field value (checkboxes use checked).
 */
function readFieldValue(field) {
	if (!field) {
		return undefined;
	}
	var tag = (field.localName || field.tagName || '').toLowerCase();
	if (tag.indexOf('checkbox') !== -1 || field.type === 'checkbox') {
		return !!field.checked;
	}
	return field.value;
}

/**
 * Write a toolbar field value (checkboxes use checked).
 */
function writeFieldValue(field, value) {
	if (!field || value === undefined || value === null) {
		return;
	}
	var tag = (field.localName || field.tagName || '').toLowerCase();
	if (tag.indexOf('checkbox') !== -1 || field.type === 'checkbox') {
		field.checked = !!value;
		return;
	}
	field.value = value;
}

/**
 * Read code + description from a type-ahead unit combo.
 */
export function readTypeAheadUnitState(combo) {
	if (!combo) {
		return {
			value: null,
			description: null
		};
	}
	var selected = combo.selectedItem;
	var description = null;
	if (selected && selected.description) {
		description = selected.description;
	} else if (combo.filter) {
		description = combo.filter;
	}
	return {
		value: combo.value,
		description: description
	};
}

/**
 * Resolve a unit lookup row by code (id).
 * @param {Function} getUnitsFn (refType, paging) => Promise<{data:[]}>
 */
export function resolveUnitByCode(getUnitsFn, refType, unitId) {
	if (!getUnitsFn || !unitId || unitId === 'UNREF') {
		return Promise.resolve(null);
	}
	return getUnitsFn(refType, {
		max: 1,
		filterField: 'code',
		filterValue: unitId
	}).then(function(result) {
		return result && result.data && result.data[0] ? result.data[0] : null;
	});
}

/**
 * Restore type-ahead unit combo: seed description into the filter, load suggestions, select by code.
 * @param {Object} options
 * @param {HTMLElement} options.combo
 * @param {string} options.refType
 * @param {string} options.unitId
 * @param {string} [options.description]
 * @param {Function} options.loadLookup (refType, lookupValue, selectedUnitId) => Promise
 * @param {Function} [options.resolveByCode] (refType, unitId) => Promise<{description}|null>
 */
export function restoreTypeAheadUnitState(options) {
	var unitId = options.unitId;
	var combo = options.combo;
	var refType = options.refType || 'TRACTOR';
	var loadLookup = options.loadLookup;

	if (!unitId) {
		return Promise.resolve();
	}
	if (unitId === 'UNREF') {
		return Promise.resolve(loadLookup(refType, null, unitId));
	}

	var descriptionPromise = Promise.resolve(options.description || null);
	if (!options.description && options.resolveByCode) {
		descriptionPromise = options.resolveByCode(refType, unitId).then(function(unit) {
			return unit && unit.description ? unit.description : null;
		});
	}

	return descriptionPromise.then(function(description) {
		if (combo && description) {
			if ('filter' in combo) {
				combo.filter = description;
			}
			if (combo.inputElement) {
				combo.inputElement.value = description;
			}
		}
		return loadLookup(refType, description || null, unitId);
	});
}

/**
 * Attach saveWorkspaceState / restoreWorkspaceState for toolbar filter fields.
 * @param {HTMLElement} component page/grid custom element
 * @param {HTMLElement} toolbarFilter toolbar containing itemid fields
 * @param {string[]} fieldIds itemids to persist
 * @param {Function} [actionRefresh] called after restore
 * @param {Object} [options]
 * @param {Function} [options.save] custom save(state, helpers) to enrich state
 * @param {Function} [options.restore] custom restore(data, helpers); skip default field write + refresh when provided
 */
export function attachWorkspaceFilterState(component, toolbarFilter, fieldIds, actionRefresh, options) {
	options = options || {};

	component.saveWorkspaceState = function() {
		var state = {};
		(fieldIds || []).forEach(function(fieldId) {
			var field = ElementUtil.query(toolbarFilter, fieldId);
			if (field) {
				state[fieldId] = readFieldValue(field);
			}
		});
		if (options.save) {
			options.save(state, {
				toolbarFilter: toolbarFilter,
				readField: readFieldValue
			});
		}
		return state;
	};

	component.restoreWorkspaceState = function(filters) {
		if (!filters) {
			return;
		}
		// Multi-component panes nest state by localName; single grids stay flat.
		var data = filters;
		if (component.localName && filters[component.localName]
				&& typeof filters[component.localName] === 'object') {
			data = filters[component.localName];
		}
		if (options.restore) {
			options.restore(data, {
				toolbarFilter: toolbarFilter,
				fieldIds: fieldIds,
				actionRefresh: actionRefresh,
				writeField: writeFieldValue,
				readField: readFieldValue
			});
			return;
		}
		(fieldIds || []).forEach(function(fieldId) {
			if (data[fieldId] !== undefined && data[fieldId] !== null) {
				writeFieldValue(ElementUtil.query(toolbarFilter, fieldId), data[fieldId]);
			}
		});
		if (actionRefresh) {
			actionRefresh();
		}
	};

	var pendingFilters = takePendingWorkspaceFilters(component);
	if (pendingFilters) {
		component.restoreWorkspaceState(pendingFilters);
	}
}

/**
 * Pending filters may be stashed on this component or an ancestor pane/tab host.
 */
function takePendingWorkspaceFilters(component) {
	var node = component;
	while (node) {
		if (node.pendingWorkspaceFilters) {
			var pendingFilters = node.pendingWorkspaceFilters;
			node.pendingWorkspaceFilters = null;
			return pendingFilters;
		}
		if (node.classList && node.classList.contains('page-pane')) {
			break;
		}
		node = node.parentElement;
	}
	return null;
}

/**
 * Find all elements in a pane body that implement workspace save/restore.
 */
export function findWorkspaceCapableElements(paneElement) {
	var body = paneElement && paneElement.querySelector
		? paneElement.querySelector('.page-body') || paneElement
		: null;
	if (!body) {
		return [];
	}
	var capable = [];
	var nodes = body.querySelectorAll('*');
	for (var i = 0; i < nodes.length; i++) {
		var node = nodes[i];
		if (typeof node.saveWorkspaceState === 'function'
				|| typeof node.restoreWorkspaceState === 'function'
				|| node.pendingWorkspaceFilters) {
			capable.push(node);
		}
	}
	return capable;
}
